import React, { useState, useMemo } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { useTestMasters } from '../../hooks/useTestMasters';

const HealthGoalsList = ({ goals, onEdit, onDelete }) => {
  const { allTestMasters } = useTestMasters();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Create a map of biomarker IDs to names
  const biomarkerIdToNameMap = useMemo(() => {
    const map = {};
    if (allTestMasters && Array.isArray(allTestMasters)) {
      allTestMasters.forEach(test => {
        if (test.biomarker_name) {
          const id = test._id || test.id || test.biomarker_id;
          if (id && !map[id]) {
            map[id] = test.biomarker_name;
          }
        }
      });
    }
    return map;
  }, [allTestMasters]);

  const sortedGoals = [...goals].sort((a, b) => a.id - b.id);
  const totalPages = Math.ceil(sortedGoals.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentGoals = sortedGoals.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">ID</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Goal Name</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Biomarkers</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentGoals.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                  No health goals found
                </td>
              </tr>
            ) : (
              currentGoals.map((goal) => (
                <tr key={goal.id} className="border-b hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 text-sm text-gray-900">{goal.id}</td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{goal.goal_name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {goal.biomarkers && goal.biomarkers.length > 0 ? (
                        <>
                          {goal.biomarkers.slice(0, 2).map((biomarkerId, idx) => {
                            const biomarkerName = biomarkerIdToNameMap[biomarkerId] || `Biomarker ${biomarkerId}`;
                            return (
                              <span
                                key={idx}
                                className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium"
                              >
                                {biomarkerName}
                              </span>
                            );
                          })}
                          {goal.biomarkers.length > 2 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">
                              +{goal.biomarkers.length - 2}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      goal.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {goal.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => onEdit(goal)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Edit"
                      >
                        <FaEdit size={16} />
                      </button>
                      <button
                        onClick={() => onDelete(goal.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete"
                      >
                        <FaTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 py-4 border-t">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-1 border rounded ${
                currentPage === page
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'hover:bg-gray-50'
              }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default HealthGoalsList;
